import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';

if (!Math.log2) {
  // tslint:disable-next-line:typedef
  Math.log2 = function(n) { return Math.log(n) / Math.log(2); };
}

export interface CharsetGroups {
  number: boolean;
  lower: boolean;
  upper: boolean;
  punctuation: boolean;
  symbol: boolean;
  other?: string;
}

export interface PasswordStrengthResult {
  charsetSize: number;
  commonPassword: boolean;
  nistEntropyBits: number;
  passwordLength: number;
  shannonEntropyBits: number;
  strengthCode: 'VERY_WEAK'|'WEAK'|'REASONABLE'|'STRONG'|'VERY_STRONG';
  trigraphEntropyBits: number;
  charsets: CharsetGroups;
}

/**
 * Determine the strength of a password.
 *
 * Usage:
 *
 *     ps = new PasswordStrength();
 *
 *     // Improve checks by adding additional data files - optional
 *     ps.addCommonPasswords(pass);  // data/common-passwords.json
 *     ps.addTrigraphMap(tri);  // data/trigraphs.json
 *
 *     // Calculate password strength
 *     stats = ps.check("my password");
 *
 * @author https://github.com/tests-always-included/password-strength/blob/master/lib/password-strength.js
 */
@Injectable()
export class PasswordStrengthService {
  private commonPasswords: string[];
  private trigraph: { [key: string]: number };
  private charsets: { number: string; lower: string; upper: string; punctuation: string; symbol: string; };

  constructor(private http: HttpClient) {
    this.commonPasswords = undefined;
    this.trigraph = undefined;
    this.charsets = {
      number: '0123456789',
      lower: 'abcdefghijklmnopqrstuvwxyz',
      upper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
      punctuation: '!\'.,:;?&-" ',    // Things found in sentences
      symbol: '@#$%^*(){}[]><~`_+=|/' // Typically math-related
    };
  }

  loadCommonPassword(url: string): Observable<{}> {
    return this.http.get<any>(url).pipe(
      // tslint:disable-line
      tap(passwords => this.addCommonPasswords(passwords)),
      map(() => ({})),
    );
  }

  loadTrigraphMap(url: string): Observable<{}> {
    return this.http.get<{}>(url).pipe(
      tap(trigraphMap => this.addTrigraphMap(trigraphMap)),
      map(() => ({})),
    );
  }

  /**
   * Sets commonPassword property with what a user passes in.
   * Uses it if it's an array, processes it into an array if it's a
   * string and throws an error if it's not a format we can work with.
   *
   * Returns the PasswordCheck object so it can be chained
   */
  addCommonPasswords(passwords: string[]|string): PasswordStrengthService {
    if (passwords) {
      if (Array.isArray(passwords)) {
        this.commonPasswords = passwords;
      } else if (typeof passwords === 'string') {
        this.commonPasswords = passwords.split(/\r\n|\r|\n/);
      } else {
        throw new Error('Format does not match any expected format.');
      }
    } else {
      this.commonPasswords = [];
    }

    return this;
  }

  /**
   * TrigraphMap is an object representing entries of 3 letter
   * combinations and how often they appear.
   *
   * {
   *     "_ty": 123,
   *     "tyl": 22,
   *     "yle": 19,
   *     "ler": 5,
   *     "er_": 543
   * }
   *
   * For further explanation on the numbers, check out the documentation
   * in the data folder.
   *
   * @typedef {Object} PasswordStrength~trigraphMap
   * @property {number} * Frequencies of that letter combo.
   *
   * Returns the PasswordStrength object so it can be chained
   */
  addTrigraphMap(trigraphMap: { [key: string]: number }): PasswordStrengthService {
    this.trigraph = trigraphMap;
    return this;
  }

  /**
   * Calculates a bunch of statistics about a given password.
   */
  check(currentPassword: string): PasswordStrengthResult {
    const result: PasswordStrengthResult = {
      charsetSize: 0,
      commonPassword: false,
      nistEntropyBits: 0,
      passwordLength: 0,
      shannonEntropyBits: 0,
      strengthCode: undefined,
      trigraphEntropyBits: undefined,
      charsets: undefined
    };

    if (!currentPassword || currentPassword.length === 0) {
      if (this.trigraph) {
        result.trigraphEntropyBits = 0;
      }

      return result;
    }

    result.commonPassword = this.checkCommonPasswords(currentPassword);
    result.charsets = this.charsetGroups(currentPassword);
    result.charsetSize = this.charsetSize(result.charsets);
    result.nistEntropyBits = this.nistScore(currentPassword);
    result.shannonEntropyBits = this.shannonScore(currentPassword);
    result.passwordLength = currentPassword.length;
    result.trigraphEntropyBits = this.checkTrigraph(currentPassword, result.charsetSize);
    result.strengthCode = this.determineStrength(result);

    return result;
  }

  /**
   * Gets the character set size of the current password.
   * This breaks up the each character set and tests the password
   * to see which set they belong in.
   */
  private charsetGroups(passToCheck: string): CharsetGroups {
    const group: CharsetGroups = { number: false, lower: false, upper: false, punctuation: false, symbol: false };
    _.forOwn(this.charsets, (value, key) => { group[key] = this.safeCharMatcher(value).test(passToCheck); });
    group.other = this.otherChars(passToCheck);
    return group;
  }

  /**
   * Calculates the size of each flag found for a password,
   * and will get the flags if a password was passed in.
   */
  private charsetSize(group: CharsetGroups): number {
    let size = 0;
    _.forOwn(this.charsets, (value, key) => {
      if (group[key]) {
        size += value.length;
      }
    });
    if (group.other) {
      size += group.other.length;
    }

    return size;
  }

  /**
   * This checks to see if the password is in the common list of
   * passwords.  If in the common passwords lists, sets the warn flag.
   */
  private checkCommonPasswords(passToCheck: string): boolean {
    passToCheck = passToCheck.toLowerCase();
    return _.indexOf(this.commonPasswords, passToCheck) > -1;
  }

  /**
   * Compares the password to the trigraph to get the rating of how
   * guessable the password is.
   */
  private checkTrigraph(passToCheck: string, charsetSize: number): number {
    if (!this.trigraph) {
      return undefined;
    }

    let score = 1;
    passToCheck = passToCheck.toLowerCase().replace(/[\W_]/gi, ' ').trim();
    passToCheck = '_' + passToCheck + '_';
    for (let i = 0; i < passToCheck.length - 2; i++) {
      const str = passToCheck.substr(i, 3);
      if (this.trigraph[str]) {
        // Less than fully random
        score *= (1.0 - this.trigraph[str] / 10000) * charsetSize;
      } else {
        // Fully random selection
        score *= charsetSize;
      }
    }

    return Math.log2(score);
  }

  /**
   * Takes parameters and calculates the strength of a given password
   * from a list of codes set. This will use the trigraph entropy bits if
   * available and fallback to the shannon entropy bits which we should
   * always have.
   */
  private determineStrength(status: PasswordStrengthResult): 'VERY_WEAK'|'WEAK'|'REASONABLE'|'STRONG'|'VERY_STRONG' {
    const entropyBits = status.trigraphEntropyBits || status.shannonEntropyBits;
    if (entropyBits <= 32) {
      // Keep out a typical attacker for minutes
      return 'VERY_WEAK';
    } else if (entropyBits <= 48) {
      // Crackable by a typical home computer in a week.
      return 'WEAK';
    } else if (entropyBits <= 64) {
      // A specialized computer could get this in one year.
      return 'REASONABLE';
    } else if (entropyBits <= 80) {
      // Resistant to a large, coordinated attack (botnet) for over a year.
      return 'STRONG';
    } else {
      // Nearly impossible to brute force, given more than all of the computing power in the world,
      // optimized algorithms, specialized hardware and a thousand years.
      return 'VERY_STRONG';
    }
  }

  /**
   * Calculates the NIST score based on the following rules from NIST
   * Special Publication 800-63 as of June 2004.
   *
   * * The entropy of the first character is four bits;
   * * The entropy of the next seven characters are two bits per
   *   character;
   * * The ninth through the twentieth character has 1.5 bits of
   *   entropy per character;
   * * Characters 21 and above have one bit of entropy per character.
   * * A "bonus" of six bits is added if both upper case letters and
   *   non-alphabetic characters are used.
   * * A "bonus" of six bits is added for passwords of length 1 through
   *   19 characters following an extensive dictionary check to ensure
   *   the password is not contained within a large dictionary.
   *   Passwords of 20 characters or more do not receive this bonus
   *   because it is assumed they are pass-phrases consisting of
   *   multiple dictionary words.
   *
   * According to Wikipedia, this score might not be overly useful.
   *
   * > Later research into human-selected password entropy using newly
   * > available real world data has demonstrated that the NIST scheme
   * > does not provide a valid metric for entropy estimation of
   * > human-selected passwords.
   */
  private nistScore(password: string): number {
    let score = 0, len = password.length;
    if (len > 20) {
      score += len - 20;
      len = 20;
    }

    if (len > 8) {
      score += 1.5 * (len - 8);
      len = 8;
    }

    if (len > 1) {
      score += 2 * (len - 1);
      len = 1;
    }

    if (len) {
      score += 4;
    }

    if (password.match(/[A-Z]/) && password.match(/[^A-Za-z]/)) {
      score += 6;
    }

    // Don't have a large dictionary, thus nobody gets the 6 bit
    // bonus.
    return score;
  }

  /**
   * Calls function to see if there are other characters used
   * which we don't explicitly check for.
   */
  private otherChars(passToCheck: string): string {
    const chars = _.values(this.charsets).join('');
    const pattern = this.safeCharMatcher(chars, 'g');
    // tslint:disable-next-line:no-any
    const hash: any = {};
    passToCheck.replace(pattern, '').split('').forEach(letter => (hash[letter] = true));
    return _.keys(hash).join('');
  }

  /**
   * Calculate the Shannon Entropy value of a password.  This is really
   * the number of bits necessary to represent the possible characters
   * times the length of the password.
   *
   * Reworked from code under a MIT license.
   * entropy.js MIT License © 2014 James Abney http://github.com/jabney
   */
  private shannonScore(pass: string): number {
    let sum = 0;
    const passLength = pass.length, freq = this.countFrequencies(pass, passLength);
    _.forOwn(freq, value => {
      const score = value / passLength;
      sum -= score * Math.log2(score);
    });
    return sum * passLength;
  }

  // tslint:disable-next-line:no-any
  private countFrequencies(pass: string, passLength: number): any {
    // tslint:disable-next-line:no-any
    const hash: any = {};
    for (let i = 0; i < passLength; i++) {
      const c = pass.charAt(i);
      _.set(hash, c, _.get(hash, c, 0) + 1);
    }

    return hash;
  }

  /**
   * Turns a list of characters into a regular expression that will match
   * whatever you pass in.  There may be trouble generating the pattern,
   * so here we are escaping every character that isn't alphanumeric.
   */
  private safeCharMatcher(letters: string, flags?: string): RegExp {
    // tslint:disable-next-line:typedef
    return new RegExp('[' + letters.replace(/[\W_]/g, function(x) { return '\\' + x; }) + ']', flags || '');
  }
}
